import {
  CustomFieldType,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import {
  buildContactCustomFieldPresenceOutcomeDefinition,
  buildFunnelOutcomeDefinition,
  buildRevenueOutcomeDefinition,
  contactCustomFieldPresenceBucketId,
  greenRedYellowResultTypeCategory,
  type ContactCustomFieldPresenceBucket,
  type ContactCustomFieldPresenceOutcomeDefinition,
  type FunnelOutcomeDefinition,
  type FunnelOutcomeStage,
  type OutcomeDefinition,
  type RevenueOutcomeDefinition,
  revenueOutcomeResults,
} from "@repo/app-types/entities/outcome";

/**
 * `amount` persists the revenue outcome: a numeric field above 0 is won, 0 or
 * less is lost, and blank is pending. It is offered only for Number fields.
 */
export type MappingMode = "specific" | "presence" | "amount";

export type RuleMappingMode = Exclude<MappingMode, "amount">;

export function getDefaultMappingModeForFieldType(
  fieldType: CustomFieldDefinition["field_type"],
): MappingMode {
  switch (fieldType) {
    case CustomFieldType.Number:
      return "amount";
    case CustomFieldType.Date:
    case CustomFieldType.DateAndTime:
      return "presence";
    default:
      return "specific";
  }
}

/**
 * The field-backed outcome editor works in a single `stages + rules` representation for
 * the specific and presence mapping modes, then persists it as one of two field-backed outcome
 * definitions: `funnel` for exact-value mappings, or
 * `contact_custom_field_presence` for blank/filled mappings. This rule shape is
 * the editor's working model only — it is never persisted directly. In presence
 * mode the two rules point at the `filled` and `blank` stages.
 */
export interface FieldBackedOutcomeRule {
  id: string;
  condition: "equals" | "populated" | "empty";
  values: string[];
  stage_id: string | null;
}

export interface FieldBackedWorkingModel {
  mappingMode: RuleMappingMode;
  stages: readonly FunnelOutcomeStage[];
  rules: readonly FieldBackedOutcomeRule[];
}

const defaultValueStages: readonly FunnelOutcomeStage[] = [
  {
    id: "no",
    name: "Lost",
    category: greenRedYellowResultTypeCategory.red,
    values: [],
  },
  {
    id: "yes",
    name: "Won",
    category: greenRedYellowResultTypeCategory.green,
    values: [],
  },
];

const defaultBooleanStages: readonly FunnelOutcomeStage[] = [
  {
    id: "no",
    name: "Lost",
    category: greenRedYellowResultTypeCategory.red,
    values: ["false"],
  },
  {
    id: "yes",
    name: "Won",
    category: greenRedYellowResultTypeCategory.green,
    values: ["true"],
  },
];

// Presence stages carry the `filled`/`blank` bucket ids so the round trip
// between the working model and the persisted presence definition is stable.
const defaultPresenceStages: readonly FunnelOutcomeStage[] = [
  {
    id: contactCustomFieldPresenceBucketId.filled,
    name: "Won",
    category: greenRedYellowResultTypeCategory.green,
    values: [],
  },
  {
    id: contactCustomFieldPresenceBucketId.blank,
    name: "Lost",
    category: greenRedYellowResultTypeCategory.red,
    values: [],
  },
];

// Outcomes created before the Won/Lost default-copy update may still carry the
// original Yes/No labels. Treat those as untouched defaults when deciding
// whether switching mapping modes needs destructive-change confirmation.
const legacyDefaultValueStages = withLegacyYesNoLabels(defaultValueStages);
const legacyDefaultBooleanStages = withLegacyYesNoLabels(defaultBooleanStages);
const legacyDefaultPresenceStages = withLegacyYesNoLabels(
  defaultPresenceStages,
);

const defaultPresenceRules: readonly FieldBackedOutcomeRule[] = [
  {
    id: "has_value",
    condition: "populated",
    values: [],
    stage_id: contactCustomFieldPresenceBucketId.filled,
  },
  {
    id: "is_blank",
    condition: "empty",
    values: [],
    stage_id: contactCustomFieldPresenceBucketId.blank,
  },
];

/**
 * Derive the editor's working model from either persisted field-backed outcome
 * that this editor manages. Funnel outcomes map to `specific` mode with one
 * `equals` rule per stage; presence outcomes map to `presence` mode with the
 * fixed filled/blank rule pair.
 */
export function getFieldBackedWorkingModel(
  outcome:
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition,
): FieldBackedWorkingModel {
  if (isPresenceOutcome(outcome)) {
    return {
      mappingMode: "presence",
      stages: [
        {
          id: contactCustomFieldPresenceBucketId.filled,
          name: outcome.filled.label,
          category: outcome.filled.category,
          values: [],
        },
        {
          id: contactCustomFieldPresenceBucketId.blank,
          name: outcome.blank.label,
          category: outcome.blank.category,
          values: [],
        },
      ],
      rules: defaultPresenceRules,
    };
  }

  return {
    mappingMode: "specific",
    stages: outcome.stages,
    rules: outcome.stages.map((stage) => ({
      id: `equals_${stage.id}`,
      condition: "equals",
      values: stage.values ?? [],
      stage_id: stage.id,
    })),
  };
}

function isPresenceOutcome(
  outcome:
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition,
): outcome is ContactCustomFieldPresenceOutcomeDefinition {
  return "filled" in outcome && "blank" in outcome;
}

function isPresenceRuleSet(rules: readonly FieldBackedOutcomeRule[]): boolean {
  return rules.some((rule) => rule.condition !== "equals");
}

function stageToBucket(
  stage: FunnelOutcomeStage | undefined,
  fallbackCategory: ContactCustomFieldPresenceBucket["category"],
  fallbackLabel: string,
): ContactCustomFieldPresenceBucket {
  const label = stage?.name.trim();
  return {
    category: stage?.category ?? fallbackCategory,
    label: label && label.length > 0 ? label : fallbackLabel,
  };
}

function stageForCondition(
  stages: readonly FunnelOutcomeStage[],
  rules: readonly FieldBackedOutcomeRule[],
  condition: FieldBackedOutcomeRule["condition"],
): FunnelOutcomeStage | undefined {
  const rule = rules.find((current) => current.condition === condition);
  return rule?.stage_id
    ? stages.find((stage) => stage.id === rule.stage_id)
    : undefined;
}

/**
 * Build the persisted outcome definition from the editor's working model. A
 * presence rule set produces a `contact_custom_field_presence` outcome; any
 * other rule set produces an exact-value `funnel` outcome.
 */
export function buildOutcomeForWorkingModel(
  customFieldDefinitionId: string,
  stages: readonly FunnelOutcomeStage[],
  rules: readonly FieldBackedOutcomeRule[],
): OutcomeDefinition {
  // The editor keeps exactly one green result at all times (clearing or
  // deleting the last green row is blocked), so `stages` is committed as-is and
  // the "exactly one green" invariant is never silently reassigned here.
  if (isPresenceRuleSet(rules)) {
    return buildContactCustomFieldPresenceOutcomeDefinition({
      customFieldDefinitionId,
      filled: stageToBucket(
        stageForCondition(stages, rules, "populated"),
        greenRedYellowResultTypeCategory.green,
        "Won",
      ),
      blank: stageToBucket(
        stageForCondition(stages, rules, "empty"),
        greenRedYellowResultTypeCategory.red,
        "Lost",
      ),
    });
  }

  // Exact-value mode edits the matching CRM values on each rule (mirroring how
  // getCrmWorkingModel seeds a rule's values from its stage). Project those
  // values back onto their stage so they persist — a funnel outcome carries its
  // values on `stages`, so building from the stages alone would drop every
  // value the user just entered.
  const valuesByStageId = new Map(
    rules
      .filter((rule) => rule.stage_id !== null)
      .map((rule) => [rule.stage_id, rule.values] as const),
  );
  return buildFunnelOutcomeDefinition({
    customFieldDefinitionId,
    stages: stages.map((stage) => ({
      ...stage,
      values: valuesByStageId.get(stage.id) ?? stage.values,
    })),
  });
}

export function buildDefaultOutcomeForMappingMode(
  customFieldDefinitionId: string,
  mappingMode: MappingMode,
  usesBooleanDefaults: boolean,
): OutcomeDefinition {
  if (mappingMode === "amount") {
    return buildRevenueOutcomeDefinition(customFieldDefinitionId);
  }

  if (mappingMode === "presence") {
    return buildContactCustomFieldPresenceOutcomeDefinition({
      customFieldDefinitionId,
      filled: {
        category: greenRedYellowResultTypeCategory.green,
        label: "Won",
      },
      blank: {
        category: greenRedYellowResultTypeCategory.red,
        label: "Lost",
      },
    });
  }

  return buildFunnelOutcomeDefinition({
    customFieldDefinitionId,
    stages: usesBooleanDefaults ? defaultBooleanStages : defaultValueStages,
  });
}

/**
 * Upgrade the original Yes/No CRM defaults to the current Won/Lost language.
 * Match the fixed default ids/categories as well as the labels so a genuinely
 * custom Yes/No outcome is not rewritten merely because its copy is similar.
 */
export function normalizeLegacyCrmOutcomeDefaults(
  outcome:
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition,
): FunnelOutcomeDefinition | ContactCustomFieldPresenceOutcomeDefinition {
  if (isPresenceOutcome(outcome)) {
    const hasLegacyDefaults =
      outcome.filled.category === greenRedYellowResultTypeCategory.green &&
      outcome.filled.label.trim().toLowerCase() === "yes" &&
      outcome.blank.category === greenRedYellowResultTypeCategory.red &&
      outcome.blank.label.trim().toLowerCase() === "no";
    if (!hasLegacyDefaults) return outcome;

    return preserveReportingTerminology(
      buildContactCustomFieldPresenceOutcomeDefinition({
        customFieldDefinitionId: outcome.custom_field_definition_id,
        filled: { ...outcome.filled, label: "Won" },
        blank: { ...outcome.blank, label: "Lost" },
      }),
      outcome,
    );
  }

  const hasLegacyWonStage = outcome.stages.some(
    (stage) =>
      stage.id === "yes" &&
      stage.category === greenRedYellowResultTypeCategory.green &&
      stage.name.trim().toLowerCase() === "yes",
  );
  const hasLegacyLostStage = outcome.stages.some(
    (stage) =>
      stage.id === "no" &&
      stage.category === greenRedYellowResultTypeCategory.red &&
      stage.name.trim().toLowerCase() === "no",
  );
  // Only upgrade the untouched two-stage default. A legacy default funnel is
  // exactly [no, yes]; requiring that length guards a customized multi-stage
  // funnel (e.g. No / Maybe / Yes) from having its rows silently renamed.
  const isUntouchedLegacyDefault =
    outcome.stages.length === 2 && hasLegacyWonStage && hasLegacyLostStage;
  if (!isUntouchedLegacyDefault) return outcome;

  const stages = outcome.stages.map((stage) => {
    if (stage.id === "yes") return { ...stage, name: "Won" };
    if (stage.id === "no") return { ...stage, name: "Lost" };
    return stage;
  });
  return preserveReportingTerminology(
    buildFunnelOutcomeDefinition({
      customFieldDefinitionId: outcome.custom_field_definition_id,
      stages,
    }),
    outcome,
  );
}

function preserveReportingTerminology<
  T extends
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition,
>(
  outcome: T,
  existingOutcome:
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition,
): T {
  return {
    ...outcome,
    singular_noun: existingOutcome.singular_noun,
    plural_noun: existingOutcome.plural_noun,
    rate_label: existingOutcome.rate_label,
  };
}

export function hasCustomizedRevenueResults(
  outcome: RevenueOutcomeDefinition,
): boolean {
  return (
    outcome.results.length !== revenueOutcomeResults.length ||
    outcome.results.some(
      (result, index) => result.label !== revenueOutcomeResults[index]?.label,
    )
  );
}

export function shouldConfirmMappingModeChange(
  stages: readonly FunnelOutcomeStage[],
  rules: readonly FieldBackedOutcomeRule[],
  mappingMode: RuleMappingMode,
): boolean {
  if (mappingMode === "presence") {
    return !(
      matchesDefaultConfiguration(stages, rules, defaultPresenceStages) ||
      matchesDefaultConfiguration(stages, rules, legacyDefaultPresenceStages)
    );
  }

  return !(
    matchesDefaultSpecificConfiguration(stages, rules, defaultValueStages) ||
    matchesDefaultSpecificConfiguration(stages, rules, defaultBooleanStages) ||
    matchesDefaultSpecificConfiguration(
      stages,
      rules,
      legacyDefaultValueStages,
    ) ||
    matchesDefaultSpecificConfiguration(
      stages,
      rules,
      legacyDefaultBooleanStages,
    ) ||
    // A default Blank or filled configuration keeps this order when it is
    // converted back to Specific values. Treat that round trip as untouched.
    matchesDefaultSpecificConfiguration(stages, rules, defaultPresenceStages) ||
    matchesDefaultSpecificConfiguration(
      stages,
      rules,
      legacyDefaultPresenceStages,
    )
  );
}

function withLegacyYesNoLabels(
  stages: readonly FunnelOutcomeStage[],
): readonly FunnelOutcomeStage[] {
  return stages.map((stage) => ({
    ...stage,
    name:
      stage.category === greenRedYellowResultTypeCategory.green ? "Yes" : "No",
  }));
}

function matchesDefaultSpecificConfiguration(
  stages: readonly FunnelOutcomeStage[],
  rules: readonly FieldBackedOutcomeRule[],
  defaults: readonly FunnelOutcomeStage[],
): boolean {
  if (stages.length !== rules.length) return false;

  const stagesById = new Map(stages.map((stage) => [stage.id, stage]));
  const defaultStagesById = new Map(defaults.map((stage) => [stage.id, stage]));
  const matchedStageIds = new Set<string>();

  const rulesAreUntouched = rules.every((rule) => {
    if (
      rule.condition !== "equals" ||
      rule.stage_id === null ||
      matchedStageIds.has(rule.stage_id)
    ) {
      return false;
    }

    const stage = stagesById.get(rule.stage_id);
    if (!stage || !arraysEqual(rule.values, stage.values)) return false;

    matchedStageIds.add(stage.id);
    const defaultStage = defaultStagesById.get(stage.id);
    if (defaultStage) {
      return (
        stage.name === defaultStage.name &&
        stage.category === defaultStage.category &&
        arraysEqual(stage.values, defaultStage.values)
      );
    }

    return (
      /^Value \d+$/.test(stage.name) &&
      stage.category === greenRedYellowResultTypeCategory.yellow &&
      stage.values.length === 0
    );
  });

  return rulesAreUntouched && matchedStageIds.size === stages.length;
}

function matchesDefaultConfiguration(
  stages: readonly FunnelOutcomeStage[],
  rules: readonly FieldBackedOutcomeRule[],
  defaults: readonly FunnelOutcomeStage[],
): boolean {
  if (stages.length !== defaults.length || rules.length !== defaults.length) {
    return false;
  }

  return defaults.every((defaultStage, index) => {
    const stage = stages[index];
    const rule = rules[index];
    return (
      stage?.id === defaultStage.id &&
      stage.name === defaultStage.name &&
      stage.category === defaultStage.category &&
      arraysEqual(stage.values, defaultStage.values) &&
      rule?.stage_id === stage.id &&
      arraysEqual(rule.values, defaultStage.values)
    );
  });
}

function arraysEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

export function getDefaultStageName(
  condition: FieldBackedOutcomeRule["condition"],
): string {
  switch (condition) {
    case "populated":
      return "Won";
    case "empty":
      return "Lost";
    case "equals":
      return "Value";
  }
}

export function getRuleConditionLabel(
  condition: FieldBackedOutcomeRule["condition"],
): string {
  switch (condition) {
    case "equals":
      return "is";
    case "populated":
      return "is filled";
    case "empty":
      return "is blank";
  }
}
